import {useEffect} from 'react'
import {IRange, editor, languages} from 'monaco-editor/esm/vs/editor/editor.api'
import {useMonaco, Monaco} from '@monaco-editor/react'
import {expressionFunctionNames} from '..'

export type ExpressionEditorAutoCompleteProviderArgs = {
  model: editor.ITextModel
  language: string
  monaco: Monaco
  word: editor.IWordAtPosition
  range: IRange | languages.CompletionItemRanges
}

export type ExpressionEditorAutoCompleteProvider = (
  args: ExpressionEditorAutoCompleteProviderArgs
) => languages.CompletionItem[]

export interface IExpressionEditorAutoCompleteContextProps {
  disabled?: boolean
  language?: string
  suggestions: ExpressionEditorAutoCompleteProvider
  children: React.ReactNode
}

export function ExpressionEditorAutoCompleteContext(
  props: IExpressionEditorAutoCompleteContextProps
) {
  const {disabled, language, suggestions, children} = props

  const monaco = useMonaco()

  useEffect(() => {
    if (disabled) return
    if (!monaco) return
    if (!language) throw new Error('language is required')
    monaco.languages.register({id: language})
    const _monarch = monaco.languages.setMonarchTokensProvider(language, {
      keywords: expressionFunctionNames,
      tokenizer: {
        root: [
          [
            /@?[a-zA-Z][\w$]*/,
            {
              cases: {
                '@keywords': 'keyword',
                '@default': 'variable',
                // '/.*?\(.*?\)/': 'function',
              },
            },
          ],
          [/".*?"/, 'string'],
          [/'.*?'/, 'string'],
          [/\d+/, 'number'],
          [/#.*$/, 'comment'],
        ],
      },
    })
    const _provider = monaco.languages.registerCompletionItemProvider(language, {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position)
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        }
        return {
          suggestions: suggestions({model, language, monaco, word, range}),
        }
      },
    })
    return () => {
      if (_monarch) _monarch.dispose()
      if (_provider) _provider.dispose()
    }
  }, [disabled, monaco, language, suggestions])

  return children
}
